import client from 'client'
import type {
  GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationRequest,
  GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationResponse,
  GetTimeSeriesReportStatsReportTimeSeriesStatsPostQueryParams,
  GetTimeSeriesReportStatsReportTimeSeriesStatsPost422,
} from '../../types/GetTimeSeriesReportStatsReportTimeSeriesStatsPost.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getTimeSeriesReportStatsReportTimeSeriesStatsPostQueryKey = (
  data?: GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationRequest,
  params?: GetTimeSeriesReportStatsReportTimeSeriesStatsPostQueryParams,
) => [{ url: '/report/time_series/stats' }, ...(params ? [params] : []), ...(data ? [data] : [])] as const

export type GetTimeSeriesReportStatsReportTimeSeriesStatsPostQueryKey = ReturnType<typeof getTimeSeriesReportStatsReportTimeSeriesStatsPostQueryKey>

/**
 * @summary Get Time Series Report Stats
 * {@link /report/time_series/stats}
 */
export async function getTimeSeriesReportStatsReportTimeSeriesStatsPost(
  data?: GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationRequest,
  params?: GetTimeSeriesReportStatsReportTimeSeriesStatsPostQueryParams,
  config: Partial<RequestConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationResponse,
    ResponseErrorConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPost422>,
    GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationRequest
  >({ method: 'POST', url: `/report/time_series/stats`, params, data, ...requestConfig })
  return res
}

export function getTimeSeriesReportStatsReportTimeSeriesStatsPostQueryOptions(
  data?: GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationRequest,
  params?: GetTimeSeriesReportStatsReportTimeSeriesStatsPostQueryParams,
  config: Partial<RequestConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationRequest>> & { client?: typeof client } = {},
) {
  const queryKey = getTimeSeriesReportStatsReportTimeSeriesStatsPostQueryKey(data, params)
  return queryOptions<
    ResponseConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationResponse>,
    ResponseErrorConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPost422>,
    ResponseConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationResponse>,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getTimeSeriesReportStatsReportTimeSeriesStatsPost(data, params, config)
    },
  })
}

/**
 * @summary Get Time Series Report Stats
 * {@link /report/time_series/stats}
 */
export function useGetTimeSeriesReportStatsReportTimeSeriesStatsPost<
  TData = ResponseConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationResponse>,
  TQueryData = ResponseConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationResponse>,
  TQueryKey extends QueryKey = GetTimeSeriesReportStatsReportTimeSeriesStatsPostQueryKey,
>(
  data?: GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationRequest,
  params?: GetTimeSeriesReportStatsReportTimeSeriesStatsPostQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationResponse>,
        ResponseErrorConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPost422>,
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: Partial<RequestConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPostMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getTimeSeriesReportStatsReportTimeSeriesStatsPostQueryKey(data, params)

  const query = useQuery({
    ...(getTimeSeriesReportStatsReportTimeSeriesStatsPostQueryOptions(data, params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetTimeSeriesReportStatsReportTimeSeriesStatsPost422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}